import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Smart Giving Guide';
const DESCRIPTION =
    'A practical guide to effective Christian donations: how to research ministries, judge transparency, and maximize the impact of every gift.';

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/4-bible.jpg',
            datePublished: '2026-07-14',
            dateModified: '2026-07-14',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: { '@id': 'https://devotionfund.com/resources/smart-giving-guide', '@type': 'WebPage' },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'How do I know if a ministry is trustworthy before donating?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Check that the ministry publishes its financials and program information openly, then cross-reference it with an independent watchdog like Charity Navigator or GuideStar. A ministry willing to answer direct questions about its finances and impact is a good sign of genuine transparency.",
                    },
                },
                {
                    '@type': 'Question',
                    name: "What's the difference between Charity Navigator and GuideStar?",
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Charity Navigator rates nonprofits on financial health, accountability, and transparency using a star system, while GuideStar (now part of Candid) focuses on providing detailed profiles, tax filings, and self-reported impact data. Using both gives you a fuller picture than either alone.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is donating appreciated stock better than giving cash?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "For many donors, yes — donating appreciated stock directly to a ministry can avoid capital gains tax you'd otherwise owe if you sold the stock first, which can make the gift more tax-efficient. Check with a tax advisor and confirm the ministry can accept stock donations.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Can I direct my donation to a specific program instead of general funds?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Many ministries let you designate a gift to a specific program — a feeding ministry, a homeless shelter, or a particular missionary — rather than the general fund. Ask the ministry directly if designation isn't offered as an option on their giving page.",
                    },
                },
            ],
        },
    ],
};

export default function SmartGivingGuide() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/smart-giving-guide' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-smart-giving-guide-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/4-bible.jpg" alt="Smart Giving" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">Smart Giving: Your Guide to Effective Christian Donations</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>In today's world, there are countless Christian ministries doing incredible work. But with so many options, how do you ensure your generous donations are used effectively? Fear not, fellow givers! Here are some tips to become a "smart giver" and maximize the impact of your contributions:</p>
                    <p><strong>Research, Research, Research:</strong></p>
                    <ul>
                        <li><strong>Mission and Values:</strong> Take time to understand the ministry's core mission and values. Do they align with your own beliefs and priorities?</li>
                        <li><strong>Transparency:</strong> Look for ministries that openly share their financial statements and program information. Reputable organizations will have a "financials" or "about us" section on their website detailing how donations are used.</li>
                        <li><strong>Charity Watchdogs:</strong> Utilize resources like Charity Navigator (<a href="https://www.charitynavigator.org/">https://www.charitynavigator.org/</a>) or GuideStar (<a href="https://www.guidestar.org/">https://www.guidestar.org/</a>) to see independent evaluations of a ministry's financial health and transparency.</li>
                    </ul>
                    <p><strong>Beyond the Financials:</strong></p>
                    <ul>
                        <li><strong>Impact Reports:</strong> Read the ministry's annual reports or impact statements. Do they clearly demonstrate how donations translate into tangible results for those they serve?</li>
                        <li><strong>Reviews and Testimonials:</strong> See what others are saying! Look for online reviews and testimonials from volunteers, donors, or program recipients.</li>
                    </ul>
                    <p><strong>Ask Questions and Get Involved:</strong></p>
                    <ul>
                        <li><strong>Contact the Ministry:</strong> Don't hesitate to directly contact the ministry with any questions you may have about their programs and finances. Their willingness to answer your inquiries reflects their commitment to transparency.</li>
                        <li><strong>Consider Volunteering:</strong> Sometimes, the best way to understand a ministry is to get involved. Volunteering your time allows you to see their work firsthand and feel confident about your support.</li>
                    </ul>
                    <p><strong>Matching Your Donation to Your Values:</strong></p>
                    <ul>
                        <li><strong>Specific Programs:</strong> Many ministries allow you to designate your donation to a specific program that aligns with your interests, like a children's feeding program or a homeless shelter.</li>
                    </ul>
                    <p><strong>Alternative Giving Methods:</strong></p>
                    <ul>
                        <li><strong>Stock Donations:</strong> Donating appreciated stock can be a tax-efficient way to support a ministry.</li>
                        <li><strong>Matching Gifts:</strong> Check with your employer to see if they offer matching gift programs that can double your donation.</li>
                    </ul>
                    <p><strong>Remember:</strong></p>
                    <p>Smart giving isn't about micromanaging, but about being informed and confident in your choices. By following these tips, you can ensure your donations make a real difference in the lives of others and glorify God through your generosity.</p>
                    <p><strong>Bonus Tip:</strong> Prayer is a powerful tool! Pray for the ministries you support and the wisdom to be a wise steward of your resources.</p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/smart-giving-guide" />
                </div>
            </div>
            <Footer />
        </div>
    );
}


